import { City, Country } from 'country-state-city';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { FiCheck, FiChevronDown, FiChevronLeft, FiX as FiCross, FiEdit3, FiX } from 'react-icons/fi';
import PhoneInput from 'react-phone-input-2';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-phone-input-2/lib/style.css';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import * as Yup from 'yup';
import { BASE_URL } from '../../utils/constants';
import { getAuthContext } from '../../utils/jwt';
import NewSegmentForm from './NewSegmentForm';

const FiChevronLeftComponent = FiChevronLeft as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiEdit3Component = FiEdit3 as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiChevronDownComponent = FiChevronDown as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiXComponent = FiX as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCheckComponent = FiCheck as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;
const FiCrossComponent = FiCross as unknown as React.ComponentType<{
  size?: number | string;
  className?: string;
}>;

interface ApiSegment {
  id: string;
  name: string;
  type: string;
  description?: string | null;
  rulesJson: any[];
}

// Interface for tag with timestamp
interface TagWithTimestamp {
  name: string;
  createdAt: number; // timestamp when the tag was added/created
}

const AddAudience: React.FC = () => {
  const navigate = useNavigate();
  const { segment } = useParams<{ segment: string }>();
  const { state } = useLocation();

  const currentSegment = state?.segmentData;
  const formattedSegment = segment?.replace(/-/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());

  // Check if segment is dynamic or static
  const isStaticSegment = currentSegment?.type?.toUpperCase() === 'STATIC';

  // State for segments
  const [allSegments, setAllSegments] = useState<ApiSegment[]>([]);
  const [loadingSegments, setLoadingSegments] = useState(false);
  const [selectedSegments, setSelectedSegments] = useState<string[]>([]);
  const [isSegmentDropdownOpen, setIsSegmentDropdownOpen] = useState(false);

  // State for tags
  const [allTags, setAllTags] = useState<TagWithTimestamp[]>([]);
  const [loadingTags, setLoadingTags] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [isTagsDropdownOpen, setIsTagsDropdownOpen] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [isAddingTag, setIsAddingTag] = useState(false);
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [tagValidationError, setTagValidationError] = useState('');

  // State for country, city, and phone
  const [countries, setCountries] = useState<any[]>([]);
  const [cities, setCities] = useState<any[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [phone, setPhone] = useState('');

  const segmentDropdownRef = useRef<HTMLDivElement>(null);
  const tagsDropdownRef = useRef<HTMLDivElement>(null);

  // ==================== FETCH COUNTRIES ====================
  useEffect(() => {
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);
  }, []);

  // ==================== FETCH CITIES WHEN COUNTRY CHANGES ====================
  useEffect(() => {
    if (selectedCountry) {
      const citiesOfCountry = City.getCitiesOfCountry(selectedCountry) || [];
      setCities(citiesOfCountry);
    } else {
      setCities([]);
    }
  }, [selectedCountry]);

  // ==================== FETCH STATIC SEGMENTS ONLY ====================
  const fetchSegments = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    setLoadingSegments(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/segments?page=0&size=100&type=STATIC`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const segments = data.content || [];

      const seenNames = new Set();
      const uniqueSegments = segments.filter((seg: ApiSegment) => {
        const duplicate = seenNames.has(seg.name);
        seenNames.add(seg.name);
        return !duplicate;
      });

      setAllSegments(uniqueSegments);
    } catch (error) {
      // console.error('❌ Failed to fetch segments:', error);
      toast.error('Failed to load segments');
    } finally {
      setLoadingSegments(false);
    }
  }, []);

  // ==================== FETCH TAGS ====================
  const fetchTags = useCallback(async () => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) return;

    setLoadingTags(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
        method: 'GET',
        headers: {
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const data = await response.json();
      const tags = Array.isArray(data) ? data : [];

      const uniqueTags = tags.filter((tag: string, index: number, self: string[]) =>
        index === self.indexOf(tag)
      );

      const tagsWithTimestamps: TagWithTimestamp[] = uniqueTags.map((tag, index) => ({
        name: tag,
        createdAt: Date.now() - (uniqueTags.length - index) * 1000,
      }));

      tagsWithTimestamps.sort((a, b) => b.createdAt - a.createdAt);

      setAllTags(tagsWithTimestamps);
    } catch (error) {
      // console.error('❌ Failed to fetch tags:', error);
      toast.error('Failed to load tags');
    } finally {
      setLoadingTags(false);
    }
  }, []);

  useEffect(() => {
    fetchSegments();
    fetchTags();
  }, [fetchSegments, fetchTags]);

  // ==================== CLICK OUTSIDE HANDLER ====================
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (segmentDropdownRef.current && !segmentDropdownRef.current.contains(event.target as Node)) {
        setIsSegmentDropdownOpen(false);
      }
      if (tagsDropdownRef.current && !tagsDropdownRef.current.contains(event.target as Node)) {
        setIsTagsDropdownOpen(false);
        setIsAddingTag(false);
        setNewTagName('');
        setTagValidationError('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // ==================== TOGGLE TAGS DROPDOWN ====================
  const toggleTagsDropdown = () => {
    const newState = !isTagsDropdownOpen;

    if (!newState) {
      setIsAddingTag(false);
      setNewTagName('');
      setTagValidationError('');
    }

    setIsTagsDropdownOpen(newState);
  };

  // ==================== CREATE NEW TAG ====================
  const handleCreateTag = async () => {
    const trimmedTag = newTagName.trim();

    if (!trimmedTag) {
      toast.error('Please enter a tag name');
      return;
    }

    const tagExists = allTags.some(
      existingTag => existingTag.name.toLowerCase() === trimmedTag.toLowerCase()
    );

    if (tagExists) {
      setTagValidationError('Tag already exists');
      return;
    }

    setTagValidationError('');

    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    setIsCreatingTag(true);

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/tags`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ name: trimmedTag }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      toast.success(`Tag "${trimmedTag}" created successfully!`);

      const newTag: TagWithTimestamp = {
        name: trimmedTag,
        createdAt: Date.now(),
      };
      setAllTags(prev => [newTag, ...prev]);
      setSelectedTags(prev => [...prev, trimmedTag]);

      setIsAddingTag(false);
      setNewTagName('');

      setTimeout(() => {
        fetchTags();
      }, 2000);

    } catch (error: any) {
      // console.error('❌ Failed to create tag:', error);
      toast.error(error.message || 'Failed to create tag');
      fetchTags();
    } finally {
      setIsCreatingTag(false);
    }
  };

  // ==================== TOGGLE SEGMENT SELECTION ====================
  const toggleSegment = (segmentId: string) => {
    setSelectedSegments(prev => {
      if (prev.includes(segmentId)) {
        return prev.filter(id => id !== segmentId);
      } else {
        return [...prev, segmentId];
      }
    });
  };

  const removeSegment = (segmentId: string) => {
    setSelectedSegments(prev => prev.filter(id => id !== segmentId));
  };

  const toggleTag = (tagName: string) => {
    setSelectedTags(prev => {
      if (prev.includes(tagName)) {
        return prev.filter(t => t !== tagName);
      } else {
        return [...prev, tagName];
      }
    });
  };

  const removeTag = (tagName: string) => {
    setSelectedTags(prev => prev.filter(t => t !== tagName));
  };

  // ==================== VALIDATION SCHEMA ====================
  const validationSchema = Yup.object({
    firstName: Yup.string()
      .required('First name is required')
      .min(2, 'First name must be at least 2 characters'),
    lastName: Yup.string()
      .required('Last name is required')
      .min(2, 'Last name must be at least 2 characters'),
    email: Yup.string()
      .email('Invalid email format')
      .required('Email is required'),
    phone: Yup.string().required('Phone number is required'),
    dob: Yup.string(),
    gender: Yup.string(),
    address: Yup.string(),
    country: Yup.string(),
    city: Yup.string(),
    dedupKey: Yup.string()
      .required('Dedup key is required')
      .oneOf(['EMAIL', 'PHONE'], 'Invalid dedup key'),
  });

  // ==================== INITIAL VALUES ====================
  const getInitialValues = () => {
    return {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      dob: '',
      gender: '',
      address: '',
      country: '',
      city: '',
      dedupKey: 'EMAIL',
    };
  };

  // ==================== API PAYLOAD ====================
  const handleSubmit = async (values: any) => {
    const { token, tenantId, userId } = getAuthContext();
    if (!token) {
      toast.error('Authentication required');
      return;
    }

    const payload: any = {
      firstName: values.firstName,
      lastName: values.lastName,
      dedupKey: values.dedupKey,
      email: values.email,
      phone: values.phone,
      dob: values.dob || '',
      gender: values.gender || '',
      status: 'ACTIVE',
      lifecycleStage: 'SUBSCRIBER',
      tags: selectedTags,
    };

    if (selectedSegments.length > 0) {
      payload.segments = selectedSegments;
    } else if (currentSegment?.id && isStaticSegment) {
      payload.segments = [currentSegment.id];
    } else {
      payload.segments = [];
    }

    if (values.address?.trim() || selectedCountry || selectedCity) {
      payload.address = {
        line1: values.address?.trim() || '',
        city: selectedCity || '',
        country: selectedCountry || '',
      };
    }

    try {
      const response = await fetch(`${BASE_URL}/v1/audience-insights/contacts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Tenant-Id': tenantId,
          'X-User-Id': userId || '',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `HTTP ${response.status}`);
      }

      toast.success('Audience member added successfully!');
      setTimeout(() => navigate('/audience'), 1500);
    } catch (error: any) {
      // console.error('❌ API Error:', error);
      toast.error(error.message || 'Failed to add audience member');
    }
  };

  // ==================== RENDER NEW SEGMENT FORM ====================
  if (segment === 'new-segment') {
    return <NewSegmentForm />;
  }

  return (
    <div className='p-2'>
      <button
        className='flex items-center text-sm text-teal-600 mb-4 hover:underline font-semibold'
        onClick={() => navigate('/audience')}
      >
        <FiChevronLeftComponent className='mr-1' /> Back to Audience
      </button>

      <div className='flex justify-between items-center mb-6'>
        <div>
          <h2 className='text-2xl font-bold text-[#3F5265]'>Add Audience</h2>
        </div>
      </div>

      <Formik
        initialValues={getInitialValues()}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ isValid, dirty, isSubmitting, values, setFieldValue }) => (
          <Form className='bg-white p-6 rounded-lg shadow-sm border border-gray-200'>
            <div className='grid grid-cols-2 gap-4'>
              {/* First Name */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                  FIRST NAME <span className='text-red-500'>*</span>
                </label>
                <Field name="firstName" className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500' />
                <ErrorMessage name="firstName" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* Last Name */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                  LAST NAME <span className='text-red-500'>*</span>
                </label>
                <Field name="lastName" className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500' />
                <ErrorMessage name="lastName" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* Email */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                  EMAIL <span className='text-red-500'>*</span>
                </label>
                <Field
                  type="email"
                  name="email"
                  className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                />
                <ErrorMessage name="email" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* Phone - just required, no min length */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                  PHONE <span className='text-red-500'>*</span>
                </label>
                <PhoneInput
                  country={'us'}
                  value={values.phone}
                  onChange={(value) => {
                    setPhone(value);
                    setFieldValue('phone', value, true);
                  }}
                  onBlur={() => setFieldValue('phone', values.phone, true)}
                  inputClass='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                  containerClass='w-full'
                  buttonClass='border rounded-l'
                  inputStyle={{
                    width: '100%',
                    height: '42px',
                    fontSize: '14px',
                    borderColor: '#D1D5DB',
                  }}
                  buttonStyle={{
                    borderColor: '#D1D5DB',
                    backgroundColor: '#FFFFFF',
                  }}
                  dropdownStyle={{ maxHeight: '300px' }}
                  enableSearch={true}
                />
                <ErrorMessage name="phone" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* DOB */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>DOB</label>
                <Field
                  type="date"
                  name="dob"
                  className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                />
                <ErrorMessage name="dob" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* Gender */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>GENDER</label>
                <Field
                  as="select"
                  name="gender"
                  className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                >
                  <option value="">Select</option>
                  <option value="Male">Male</option>
                  <option value="Female">Female</option>
                  <option value="Other">Other</option>
                </Field>
                <ErrorMessage name="gender" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* Address */}
              <div className='col-span-2'>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>ADDRESS</label>
                <Field
                  name="address"
                  className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                  placeholder="123 Main Street"
                />
                <ErrorMessage name="address" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* Country */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>COUNTRY</label>
                <select
                  className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                  value={selectedCountry}
                  onChange={(e) => {
                    const countryCode = e.target.value;
                    setSelectedCountry(countryCode);
                    setSelectedCity('');
                    setFieldValue('country', countryCode);
                    setFieldValue('city', '');
                  }}
                >
                  <option value="">Select Country</option>
                  {countries.map((country) => (
                    <option key={country.isoCode} value={country.isoCode}>
                      {country.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* City */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>CITY</label>
                <select
                  className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                  value={selectedCity}
                  onChange={(e) => {
                    const cityName = e.target.value;
                    setSelectedCity(cityName);
                    setFieldValue('city', cityName);
                  }}
                  disabled={!selectedCountry}
                >
                  <option value="">Select City</option>
                  {cities.map((city, index) => (
                    <option
                      key={`${city.name}-${city.stateCode || ''}-${city.countryCode || ''}-${index}`}
                      value={city.name}
                    >
                      {city.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Dedup Key - Unique ID */}
              <div>
                <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                  UNIQUE ID <span className='text-red-500'>*</span>
                </label>
                <Field
                  as="select"
                  name="dedupKey"
                  className='w-full border px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500'
                >
                  <option value="EMAIL">Email</option>
                  <option value="PHONE">Phone</option>
                </Field>
                <ErrorMessage name="dedupKey" component="div" className='text-red-500 text-xs mt-1' />
              </div>

              {/* Segments and Tags side by side */}
              <div className='col-span-2'>
                <div className='grid grid-cols-2 gap-4'>
                  <div>
                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                      SEGMENTS <span className='text-gray-400 text-xs'>(Optional)</span>
                    </label>
                    <p className='text-xs text-gray-400 mb-2'>
                      {isStaticSegment
                        ? 'Select additional segments'
                        : 'Select segments'}
                    </p>

                    {selectedSegments.length > 0 && (
                      <div className='flex flex-wrap gap-2 mb-2'>
                        {selectedSegments.map(segId => {
                          const seg = allSegments.find(s => s.id === segId);
                          return seg ? (
                            <span key={segId} className='inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded-full text-xs'>
                              {seg.name}
                              <button
                                type='button'
                                onClick={() => removeSegment(segId)}
                                className='hover:text-blue-600'
                              >
                                <FiXComponent size={14} />
                              </button>
                            </span>
                          ) : null;
                        })}
                      </div>
                    )}

                    <div className='relative' ref={segmentDropdownRef}>
                      <button
                        type='button'
                        onClick={() => setIsSegmentDropdownOpen(!isSegmentDropdownOpen)}
                        className='w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center'
                      >
                        <span className='text-gray-600 text-sm'>
                          {selectedSegments.length > 0
                            ? `${selectedSegments.length} segment${selectedSegments.length > 1 ? 's' : ''} selected`
                            : 'Select segments...'}
                        </span>
                        <FiChevronDownComponent className={`transition-transform ${isSegmentDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isSegmentDropdownOpen && (
                        <div className='absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto'>
                          {loadingSegments ? (
                            <div className='p-3 text-center text-gray-500'>Loading segments...</div>
                          ) : allSegments.length === 0 ? (
                            <div className='p-3 text-center text-gray-500'>No static segments available</div>
                          ) : (
                            allSegments.map(seg => (
                              <label key={seg.id} className='flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer'>
                                <input
                                  type='checkbox'
                                  checked={selectedSegments.includes(seg.id)}
                                  onChange={() => toggleSegment(seg.id)}
                                  className='mr-2 w-4 h-4 text-teal-600 rounded'
                                />
                                <span className='text-sm'>{seg.name}</span>
                                <span className='ml-2 text-xs text-gray-400'>
                                  ({seg.type})
                                </span>
                              </label>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <p className='text-xs text-gray-400 mt-1'>
                      {selectedSegments.length === 0 && 'No segments selected'}
                    </p>
                  </div>

                  {/* Multi-Select Tags with Add Tag feature */}
                  <div>
                    <label className='block mb-1 font-normal text-[#3F5265] text-[12px]'>
                      TAGS <span className='text-gray-400 text-xs'>(Optional)</span>
                    </label>
                    <p className='text-xs text-gray-400 mb-2'>
                      Select tags to associate
                    </p>

                    {selectedTags.length > 0 && (
                      <div className='flex flex-wrap gap-2 mb-2'>
                        {selectedTags.map(tag => (
                          <span key={tag} className='inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-800 rounded-full text-xs'>
                            {tag}
                            <button
                              type='button'
                              onClick={() => removeTag(tag)}
                              className='hover:text-purple-600'
                            >
                              <FiXComponent size={14} />
                            </button>
                          </span>
                        ))}
                      </div>
                    )}

                    <div className='relative' ref={tagsDropdownRef}>
                      <button
                        type='button'
                        onClick={toggleTagsDropdown}
                        className='w-full border border-gray-300 px-3 py-2 rounded focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white flex justify-between items-center'
                      >
                        <span className='text-gray-600 text-sm'>
                          {selectedTags.length > 0
                            ? `${selectedTags.length} tag${selectedTags.length > 1 ? 's' : ''} selected`
                            : 'Select tags...'}
                        </span>
                        <FiChevronDownComponent className={`transition-transform ${isTagsDropdownOpen ? 'rotate-180' : ''}`} />
                      </button>

                      {isTagsDropdownOpen && (
                        <div className='absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-60 overflow-y-auto'>
                          {loadingTags ? (
                            <div className='p-3 text-center text-gray-500'>Loading tags...</div>
                          ) : (
                            <>
                              {!isAddingTag ? (
                                <div
                                  className='flex items-center px-3 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100'
                                  onClick={() => {
                                    setIsAddingTag(true);
                                    setTagValidationError('');
                                  }}
                                >
                                  <span className='text-blue-600 font-medium text-sm flex items-center gap-2'>
                                    <span className='text-blue-500 text-lg'>+</span> Add Tag
                                  </span>
                                </div>
                              ) : (
                                <div className='px-3 py-2 bg-blue-50 border-b border-blue-200'>
                                  <div className='flex items-center gap-2'>
                                    <input
                                      type='text'
                                      value={newTagName}
                                      onChange={(e) => {
                                        setNewTagName(e.target.value);
                                        if (tagValidationError) {
                                          setTagValidationError('');
                                        }
                                      }}
                                      placeholder='Enter tag name...'
                                      className={`flex-1 px-2 py-1 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm ${tagValidationError ? 'border-red-500' : 'border-blue-300'
                                        }`}
                                      autoFocus
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleCreateTag();
                                        }
                                        if (e.key === 'Escape') {
                                          setIsAddingTag(false);
                                          setNewTagName('');
                                          setTagValidationError('');
                                        }
                                      }}
                                      disabled={isCreatingTag}
                                    />
                                    <button
                                      type='button'
                                      onClick={handleCreateTag}
                                      disabled={isCreatingTag || !newTagName.trim()}
                                      className={`p-1 rounded hover:bg-green-100 ${isCreatingTag || !newTagName.trim() ? 'opacity-50 cursor-not-allowed' : ''
                                        }`}
                                      title='Create tag'
                                    >
                                      <FiCheckComponent size={18} className='text-green-600' />
                                    </button>
                                    <button
                                      type='button'
                                      onClick={() => {
                                        setIsAddingTag(false);
                                        setNewTagName('');
                                        setTagValidationError('');
                                      }}
                                      className='p-1 rounded hover:bg-red-100'
                                      title='Cancel'
                                    >
                                      <FiCrossComponent size={18} className='text-red-600' />
                                    </button>
                                  </div>

                                  {tagValidationError && (
                                    <div className='text-red-500 text-xs mt-1 flex items-center gap-1'>
                                      <span>⚠️</span> {tagValidationError}
                                    </div>
                                  )}

                                  {isCreatingTag && (
                                    <div className='text-xs text-gray-500 mt-1'>Creating tag...</div>
                                  )}
                                </div>
                              )}

                              {allTags.length === 0 && !isAddingTag ? (
                                <div className='p-3 text-center text-gray-500'>No tags available</div>
                              ) : (
                                allTags.map(tagObj => (
                                  <label key={tagObj.name} className='flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer'>
                                    <input
                                      type='checkbox'
                                      checked={selectedTags.includes(tagObj.name)}
                                      onChange={() => toggleTag(tagObj.name)}
                                      className='mr-2 w-4 h-4 text-teal-600 rounded'
                                    />
                                    <span className='text-sm'>{tagObj.name}</span>
                                    {selectedTags.includes(tagObj.name) && (
                                      <span className='ml-auto text-xs text-purple-600'>✓</span>
                                    )}
                                  </label>
                                ))
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    <p className='text-xs text-gray-400 mt-1'>
                      {selectedTags.length === 0 && 'No tags selected'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Submit Buttons */}
              <div className='col-span-2 flex justify-end gap-3 mt-6 pt-4 border-t border-gray-200'>
                <button
                  type='button'
                  onClick={() => navigate('/audience')}
                  className='px-6 py-2 border rounded text-gray-600 hover:bg-gray-50'
                >
                  Cancel
                </button>
                <button
                  type='submit'
                  disabled={isSubmitting || !(isValid && dirty)}
                  className={`px-6 py-2 rounded text-white font-medium ${isValid && dirty && !isSubmitting
                    ? 'bg-[#00899E] hover:bg-[#007A8A]'
                    : 'bg-gray-300 cursor-not-allowed'
                    }`}
                >
                  {isSubmitting ? 'Saving...' : 'Save'}
                </button>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <ToastContainer position='top-right' autoClose={3000} />
    </div>
  );
};

export default AddAudience;